import type { SearchSuggestion } from '@silvertours/front-entities';
import type {
  RentalDirection,
  SearchFormState,
} from '@silvertours/front-features/src/SearchFormLegacy';
import { format } from 'date-fns/format';
import { String } from '@silvertours/front-shared';
import { GenericAnalyticsEvent } from './types';
import {
  GENERIC_EVENT_NAME,
  SENIOR_DRIVER_AGE_THRESHOLD,
  YOUNG_DRIVER_AGE_THRESHOLD,
} from './constants';

const trackEvent = async (data: GenericAnalyticsEvent) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

export const trackInfoBannerCloseClicked = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'info_banner_close',
  });
};

/* login menu events */
// Login button clicked
export const trackLoginMenuLoginClick = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'login_menu_log_in',
  });
};

// "Register new user" link clicked
export const trackLoginMenuRegisterClick = ({ isLoggedIn = false }) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'login_menu_create_account',
    properties: { isLoggedIn },
  });
};
/* login menu events end */

// Search Form/Wizard Events
export const trackCancellationHover = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'free_cancellation_tooltip_hover',
  });
};

const trackEditLocationClick = (direction: RentalDirection) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'edit_location_click',
    properties: { direction },
  });
};

const trackLocationSuggestionClick = (
  direction: RentalDirection | undefined,
  location: SearchSuggestion,
  suggestion_position: number,
) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'location_suggestion_click',
    properties: {
      direction,
      location,
      suggestion_position,
    },
  });
};

export const trackSearchHistorySelect = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'search_history_item_click',
  });
};

const trackEditTravelDateClick = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'edit_travel_date_click',
    properties: {},
  });
};

const trackEditTimeClick = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'edit_time_click',
    properties: {},
  });
};

const trackDateRangeConfirmSelectionClick = (
  drop_off_date?: Date,
  pick_up_date?: Date,
) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'date_range_confirm_selection_click',
    properties: {
      drop_off_date: drop_off_date
        ? format(drop_off_date, String.isoDateTimeWithSeconds)
        : undefined,
      pick_up_date: pick_up_date
        ? format(pick_up_date, String.isoDateTimeWithSeconds)
        : undefined,
    },
  });
};

const trackEditDriverAgeClick = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'edit_driver_age_click',
    properties: {},
  });
};

const trackReturnOnDifferentLocationClick = (is_enabled: boolean) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'return_on_different_location_click',
    properties: { is_enabled },
  });
};

const trackStartSearch = (
  search: SearchFormState | null,
  duration: number,
  isOneWay: boolean,
  subcategory?: string,
) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'start_search',
    properties: {
      ...search,
      source: 'landing_page',
      ...(subcategory && { subcategory }),
      device_date_time: format(new Date(), String.isoDateTimeWithSeconds),
      is_roundtrip: !isOneWay,
      rental_duration_in_days: duration,
      age_group: (() => {
        switch (true) {
          case search && search.customerAge <= YOUNG_DRIVER_AGE_THRESHOLD:
            return 'lower';
          case search && search.customerAge > SENIOR_DRIVER_AGE_THRESHOLD:
            return 'greater';
          default:
            return 'default';
        }
      })(),
    },
  });
};

// Account Events
const trackLogin = (payload: { userId?: string }) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'login',
    properties: payload,
  });
};

// Fired once the account session check (login state) has resolved, whether
// or not a user turned out to be logged in.
const trackAccountResolved = (payload: { userId?: string }) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'account_resolved',
    properties: payload,
  });
};

const trackLogout = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'logout',
  });
};

// marketing slider events
export const trackMarketingSliderCard = (title: string) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'marketing_slider_card_click',
    properties: { title },
  });
};

export const trackMarketingSliderDrag = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'marketing_slider_drag',
  });
};

export const trackMarketingSliderMouseWheel = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'marketing_slider_mouse_wheel',
  });
};

export const trackMarketingSliderButtonClick = (
  direction: 'forward' | 'backward',
) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'marketing_slider_arrow_click',
    properties: { direction },
  });
};

// offer box events
const trackOfferBoxStartSearch = (offer: string) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'offerbox_start_search',
    properties: { offer },
  });
};

const trackOfferBoxSliderButtonClick = (direction: 'forward' | 'backward') => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'offerbox_slider_button_clicked',
    properties: { direction },
  });
};

const trackOfferBoxSliderDragged = (offset: number) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'offerbox_slider_dragged',
    properties: { offset },
  });
};

const trackOfferBoxSliderMouseWheel = (offset: number) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'offerbox_slider_mousewheel',
    properties: { offset },
  });
};

export const trackReviewSearchClicked = (provider: string) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'customer_review_rental_overlay_detail',
    properties: { provider },
  });
};

export const trackShowCustomerReviews = (provider: string) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'customer_review_rental_overlay',
    properties: { provider },
  });
};

export const trackReviewShowDetailsClicked = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'customer_review_rental_overlay_detail',
  });
};

// content events
export const trackLinkButtonClick = ({
  label,
  href,
}: {
  label: string;
  href: string;
}) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'click_content_cta',
    properties: { label, href },
  });
};

// Main navigation list item clicked
export const trackNavigationClicked = (name: string) => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'navigation_click',
    properties: { item: name }, // name of the item clicked
  });
};

// Newsletter events
export const trackNewsletterRegistration = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'newsletter_subscription_click',
  });
};

// App promo banner
export const trackAppPromoBannerViewed = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'web2app_banner_view',
  });
};

export const trackAppPromoBannerClosed = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'web2app_banner_close',
  });
};

export const trackAppPromoBannerIosAppLinkClicked = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'web2app_banner_click_appstore',
  });
};

export const trackAppPromoBannerAndroidAppLinkClicked = () => {
  trackEvent({
    event: GENERIC_EVENT_NAME,
    name: 'web2app_banner_click_playstore',
  });
};

export {
  trackLocationSuggestionClick,
  trackReturnOnDifferentLocationClick,
  trackDateRangeConfirmSelectionClick,
  trackEditLocationClick,
  trackEditDriverAgeClick,
  trackStartSearch,
  trackLogin,
  trackAccountResolved,
  trackLogout,
  trackEditTravelDateClick,
  trackEditTimeClick,
  trackOfferBoxStartSearch,
  trackOfferBoxSliderButtonClick,
  trackOfferBoxSliderMouseWheel,
  trackOfferBoxSliderDragged,
};
